import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-block mb-8">
            <img
              src="/HRO-Logo-transparent-white-logo.png"
              alt="Horse Racing Oracle"
              className="h-12"
            />
          </Link>
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Privacy Policy
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            Last updated: 1 May 2025
          </p>
        </div>

        {/* Content */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-600">
                Horse Racing Oracle ("we", "our", or "us") is committed to protecting your privacy and personal data. 
                This privacy policy explains how we collect, process, and protect your personal information when you 
                use our automated horse race betting service.
              </p>
            </section>

            {/* Data Controller */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Controller</h2>
              <p className="text-gray-600">
                Horse Racing Oracle acts as the data controller for the personal information you provide to us. 
                We are registered with the Information Commissioner's Office (ICO) and comply with UK data 
                protection regulations, including the UK GDPR.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Account Information</h3>
                  <ul className="list-disc pl-5 text-gray-600">
                    <li>Name and email address</li>
                    <li>Contact information</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Betfair Integration Data</h3>
                  <ul className="list-disc pl-5 text-gray-600">
                    <li>Betfair API credentials and tokens</li>
                    <li>Betfair account balance information</li>
                    <li>Betting history and transaction records</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Bot Configuration Data</h3>
                  <ul className="list-disc pl-5 text-gray-600">
                    <li>Bot settings and preferences</li>
                    <li>Daily profit targets</li>
                    <li>Risk management parameters</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Usage Data</h3>
                  <ul className="list-disc pl-5 text-gray-600">
                    <li>Bot activity logs</li>
                    <li>Performance statistics</li>
                    <li>Technical logs for troubleshooting</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Service Provision</h3>
                  <ul className="list-disc pl-5 text-gray-600">
                    <li>Operating the automated betting bot</li>
                    <li>Processing bets through the Betfair API</li>
                    <li>Maintaining your account and preferences</li>
                    <li>Providing customer support</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Performance and Analytics</h3>
                  <ul className="list-disc pl-5 text-gray-600">
                    <li>Analyzing betting patterns and performance</li>
                    <li>Improving our algorithms and service</li>
                    <li>Generating performance reports</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Legal and Regulatory Compliance</h3>
                  <p className="text-gray-600">Checks such as the following are carried out as part of the Betfair account setup and verification:</p>
                  <ul className="list-disc pl-5 text-gray-600">
                    <li>Anti-money laundering (AML) checks</li>
                    <li>Fraud prevention</li>
                    <li>Regulatory reporting requirements</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Legal Basis for Processing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Legal Basis for Processing</h2>
              <div className="space-y-4">
                <p className="text-gray-600">We process your personal data on the following legal grounds:</p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>
                    <span className="font-medium">Contract:</span> Processing necessary for the performance of our 
                    contract with you to provide the betting bot service
                  </li>
                  <li>
                    <span className="font-medium">Legal Obligation:</span> Compliance with legal and regulatory 
                    requirements in the industry
                  </li>
                  <li>
                    <span className="font-medium">Legitimate Interests:</span> Improving our services, preventing 
                    fraud, and ensuring security
                  </li>
                  <li>
                    <span className="font-medium">Consent:</span> Where specifically requested for certain processing 
                    activities
                  </li>
                </ul>
              </div>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Sharing</h2>
              <div className="space-y-4">
                <p className="text-gray-600">We share your data with:</p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Betfair (you authorise access to your Betfair account via Betfair's API)</li>
                  <li>Payment processors (for subscription payments)</li>
                  <li>Cloud service providers (for hosting and operations)</li>
                  <li>Regulatory authorities (if/when legally required)</li>
                </ul>
              </div>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
              <p className="text-gray-600">
                We retain your personal data for as long as necessary to provide our services and comply with legal 
                obligations. You can request deletion of your account data at any time, subject to legal retention requirements.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
              <div className="space-y-4">
                <p className="text-gray-600">Under UK GDPR, you have the right to:</p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing</li>
                  <li>Data portability</li>
                  <li>Withdraw consent</li>
                </ul>
                <p className="text-gray-600">
                  To exercise these rights, please contact us through our{' '}
                  <Link to="/faqs" className="text-primary-600 hover:text-primary-500">
                    support page
                  </Link>
                  .
                </p>
              </div>
            </section>

            {/* Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Security</h2>
              <p className="text-gray-600">
                We implement appropriate technical and organizational measures to protect your personal data, 
                including encryption, access controls, and regular security assessments. However, no internet 
                transmission is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Tracking</h2>
              <p className="text-gray-600">
                We use essential cookies to maintain your session and preferences. We also use analytics cookies 
                to improve our service. You can control cookie preferences through your browser settings.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-600">
                For any privacy-related queries or to exercise your rights, please contact our Data Protection 
                Officer through our{' '}
                <Link to="/faqs" className="text-primary-600 hover:text-primary-500">
                  support page
                </Link>
              </p>
            </section>

            {/* Complaints */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Complaints</h2>
              <p className="text-gray-600">
                If you are unhappy with how we handle your data, you have the right to complain to the 
                Information Commissioner's Office (ICO). Visit{' '}
                <a 
                  href="https://ico.org.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-500"
                >
                  ico.org.uk
                </a>
                {' '}for more information.
              </p>
            </section>

            {/* Updates */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Policy Updates</h2>
              <p className="text-gray-600">
                We regularly review and update this privacy policy. Any changes will be posted on this page 
                with an updated revision date. Please check back periodically to stay informed about how we 
                protect your personal data.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
} 